import { useMemo, useRef, useState, type ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";
import { Col, Row } from "react-bootstrap";
import SummaryCard from "components/core/blocks/SummaryCard";
import Metric from "components/core/blocks/SummaryMetric";
import useSelectedMetrics from "./hooks/useSelectedMetrics";
import MultiMetricChart from "./MultiMetricChart";
import { saturateColor } from "utils/colors";
import { LegacyMetricSelector } from "components/core/blocks/MetricSelector";
import { extendAllEntityMetricOptions, useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { useAppSelector } from "../../../redux/hooks";
import OnboardingWorkflowProgress from "../OnboardingWorkflowProgress";
import { useModuleEnabled } from "redux/selectors/modules";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import moment from "moment";
import { FaTable } from "react-icons/fa6";
import { IoStatsChart } from "react-icons/io5";
import { type Instance } from "react-table-6";
import { MdOutlineMoreTime } from "react-icons/md";
import type { MetricColumn } from "../../../views/merchandise/columns/buildMetricColumn";
import type { SeriesLegendItemClickEventObject } from "highcharts";
import { css } from "../../../../styled-system/css";
import { FaEyeSlash } from "react-icons/fa";
import Card from "../../core/basic/Card";

const setShownMetrics = (key: string, columns: string[]) => {
  const options = localStorage.getItem("metricShowOptions");
  let parsedOptions = {};
  try {
    parsedOptions = JSON.parse(options!) as Record<string, string[]>;
  } catch {
    // noop
  }
  localStorage.setItem("metricShowOptions", JSON.stringify({ ...parsedOptions, [key]: columns }));
};

const getShownMetrics = (key: string, defaults?: string[]) => {
  const options = localStorage.getItem("metricShowOptions");
  defaults = defaults || [];
  if (options) {
    try {
      return (JSON.parse(options) as Record<string, string[]> | null)?.[key] ?? defaults;
    } catch {
      return defaults;
    }
  }
  return defaults;
};

interface Props {
  id?: string;
  title?: string;
  titleElement?: ReactNode;
  byline: ReactNode;
  data?: { date: { start_date: string }[]; total: unknown } | { start_date: string }[];
  metrics: Array<MetricColumn>;
  initialSelectedMetric: string[] | string;
  children?: ReactNode;
  small?: boolean;
  targetLabel?: string;
  loading?: boolean;
  showLoadingSpinner?: boolean;
  group?: string;
  showMetricsSelector?: boolean;
  hideMetrics?: boolean;
  defaultStateContent?: ReactNode;
  status?: ReactNode;
  inlineStatus?: ReactNode;
  showAllMetrics?: boolean;
  defaultTargetColor?: boolean;
  selectedCategory?: null;
  selectedAd?: null;
  selectedProduct?: null;
  showAnnotations?: boolean;
  hideChart?: boolean;
  context?: string;
  showSkeletonLoadingEffect?: boolean;
  rightContent?: ReactNode;
  extendMetrics?: boolean;
  cellHighlightKey?: string | null;
  cellHighlightDates?: string[] | null;
}

const MetricsCard = ({
  id,
  title,
  titleElement,
  byline,
  data,
  metrics,
  initialSelectedMetric,
  children,
  small = false,
  targetLabel = "vs. Previous",
  loading = false,
  showLoadingSpinner = false,
  group = "day",
  showMetricsSelector = false,
  hideMetrics,
  defaultStateContent,
  status,
  inlineStatus,
  showAllMetrics = false,
  defaultTargetColor = false,
  selectedCategory = null,
  selectedAd = null,
  selectedProduct = null,
  showAnnotations = false,
  hideChart = false,
  context,
  showSkeletonLoadingEffect = false,
  rightContent = null,
  extendMetrics = true,
  cellHighlightKey = null,
  cellHighlightDates = null,
}: Props) => {
  const mobileStyles = useMediaQuery("(max-width:600px)");
  const { selectedMetrics, toggleMetric, setSelectedMetrics } = useSelectedMetrics(
    Array.isArray(initialSelectedMetric) ? initialSelectedMetric : [initialSelectedMetric],
    id,
  );

  const [chartToggle, setChartToggle] = useState("chart");

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef<Instance>(null);
  const [showRightMetrics, setShowRightMetrics] = useState(false);

  const showWorkflowProgressBar = useModuleEnabled("WORKFLOW_PROGRESS");

  const standardMetricIds = useStandardEntityMetrics(context);
  const chartMetrics = useMemo(() => {
    if (!extendMetrics) {
      return metrics;
    }
    return extendAllEntityMetricOptions(standardMetricIds, data, metrics, marketPlace, "overview").reduce(
      (acc, metric) => {
        acc.push(metric);
        if (metric.prev_accessor) {
          acc.push({
            ...metric,
            id: `${metric.id}_prev`,
            isPrev: true,
            accessor: metric.prev_accessor,
            name: `${metric.name} (Previous)`,
            color: saturateColor(metric.color, 1.5),
          });
        }
        return acc;
      },
      [] as Array<MetricColumn>,
    );
  }, [metrics, data, standardMetricIds, marketPlace, extendMetrics]);

  const [showMetrics, setShowMetrics] = useState(() => {
    const initial =
      showAllMetrics ?
        chartMetrics.map((metric) => metric.id!)
      : chartMetrics
          .filter((m) => !m.isPrev)
          .slice(0, 6)
          .map((metric) => metric.id!);
    if (id) {
      return getShownMetrics(id, initial);
    }
    return initial;
  });

  const filteredMetrics = useMemo(() => {
    return showMetrics.map((id) => chartMetrics.find((metric) => metric.id === id)).filter(Boolean);
  }, [chartMetrics, showMetrics]);

  const orderedChartMetrics = useMemo(() => {
    const shown = showMetrics.map((id) => chartMetrics.find((m) => m.id === id)).filter(Boolean);
    const rest = chartMetrics.filter((m) => !showMetrics.includes(m.id!));

    return [...shown, ...rest];
  }, [chartMetrics, showMetrics]);

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const onShowMetricsChange = (showMetrics: string[]) => {
    setShowMetrics(showMetrics);
    if (id) {
      setShownMetrics(id, showMetrics);
    }
  };

  const columnSpecs = useMemo(() => {
    return filteredMetrics
      .filter((m) => !m.isPrev)
      .map((m) => {
        return {
          key: m.id,
          compareKey: null,
        };
      });
  }, [filteredMetrics]);

  const toggleButtons = [
    {
      value: "chart",
      label: <IoStatsChart />,
    },
    {
      value: "table",
      label: <FaTable />,
    },
    {
      value: "hide",
      label: <FaEyeSlash />,
    },
  ];
  if (rightContent && chartToggle === "chart") {
    toggleButtons.push({
      value: "right",
      label: <MdOutlineMoreTime />,
    });
  }

  return (
    <>
      <Row
        className={css({
          paddingY: "small",
          overflowX: "auto",
          flexWrap: "nowrap",
          ".container-fluid &": { marginBottom: "small" },
        })}
        hidden={hideMetrics}
      >
        {filteredMetrics
          .filter((m) => !m.isPrev)
          .map((m) => {
            return (
              <Col
                key={m.id}
                className={css({
                  cursor: m.clickable === false ? "inherit" : "pointer",
                  flex: "0 0 auto",
                  width: {
                    base: "50%",
                    lg: "25%",
                    xl: "16.6%",
                  },
                })}
                onClick={() => {
                  if (m.clickable === false) {
                    return;
                  }
                  toggleMetric(m.id!);
                }}
              >
                <Card className={css({ padding: "medium" })}>
                  <Metric
                    toolTip={m.toolTip}
                    name={m.name!}
                    value={m.overrideValue ?? m.actual}
                    target={m.target}
                    formatOptions={{
                      format: m.format as "number" | "percent" | "currency",
                      decimalPlaces: m.decimalPlaces,
                      compact: m.compact,
                      overrideMarketplace: m.overrideMarketplace,
                    }}
                    compareFormatOptions={m.compareFormatOptions}
                    showTarget={!!m.target}
                    lower={m.lower}
                    targetColor={
                      defaultTargetColor ? null
                      : (m.lower ? -1 * (m.target ?? 0) : (m.target ?? 0)) > 0 ?
                        "#007000"
                      : "#d2222d"
                    }
                    legendColor={m.color}
                    targetLabel={m.targetLabel || targetLabel}
                    selected={selectedMetrics.includes(m.id!)}
                    loading={showSkeletonLoadingEffect ? loading : false}
                  />
                </Card>
              </Col>
            );
          })}
      </Row>
      {showWorkflowProgressBar && <OnboardingWorkflowProgress />}
      <SummaryCard
        title={titleElement ? titleElement : title}
        byline={byline}
        status={
          <>
            {showMetricsSelector && (
              <div className="d-flex justify-content-end align-items-center">
                {inlineStatus && inlineStatus}
                <div className="mr-2">
                  <ButtonGroup
                    variant="inline"
                    buttons={toggleButtons}
                    onSelect={(value) => {
                      if (value === "right") {
                        setShowRightMetrics((prev) => !prev);
                        return;
                      }
                      setChartToggle(value);
                    }}
                    value={chartToggle}
                  />
                </div>
                <LegacyMetricSelector
                  groupMetrics={true}
                  entityType={context}
                  selectorType="overview"
                  columnOptions={extendAllEntityMetricOptions(
                    standardMetricIds,
                    data,
                    orderedChartMetrics.map((m) => ({
                      ...m,
                      label: m.name,
                      checked: showMetrics.includes(m.id!),
                    })),
                    marketPlace,
                  )}
                  onMetricChange={(updatedMetric) => {
                    const currentShownMetrics = new Set(showMetrics);

                    const nextShownMetrics = Object.entries(updatedMetric)
                      .filter(([, show]) => show)
                      .map(([metricId]) => metricId);

                    const netNewMetrics = new Set(nextShownMetrics).difference(currentShownMetrics);

                    setSelectedMetrics((currentSelectedMetrics) => {
                      return [...currentSelectedMetrics, ...Array.from(netNewMetrics)];
                    });
                    onShowMetricsChange(nextShownMetrics);
                  }}
                  onResetMetrics={() => {
                    setShowMetrics([]);
                    const defaultShowMetrics = chartMetrics
                      .filter((m) => !m.isPrev)
                      .slice(0, 6)
                      .map((metric) => metric.id!);
                    onShowMetricsChange(defaultShowMetrics);
                  }}
                  setSavedColumnOrder={(updatedMetrics) => {
                    if (!id) {
                      return;
                    }
                    const updatedOrder = Object.keys(updatedMetrics).sort((a, b) => {
                      if (updatedMetrics[a] && updatedMetrics[b]) {
                        return updatedMetrics[a] - updatedMetrics[b];
                      }
                      return 0;
                    });
                    setShownMetrics(id, updatedOrder);
                    setShowMetrics(updatedOrder);
                  }}
                />
              </div>
            )}
            {status && status}
          </>
        }
      >
        {defaultStateContent ?
          defaultStateContent
        : <Row>
            {!small && (
              <>
                {chartToggle === "chart" && (
                  <>
                    <Col xs={rightContent && showRightMetrics ? 8 : 12}>
                      {!hideChart && !!data && (
                        <MultiMetricChart
                          handleLegendChange={handleLegendChange}
                          selectedMetrics={selectedMetrics}
                          selectedCategory={selectedCategory}
                          selectedAd={selectedAd}
                          selectedProduct={selectedProduct}
                          // There's some kind of mismatch between what `accessor` can be
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          metrics={filteredMetrics as any}
                          data={"date" in data ? data.date : data}
                          mobileStyles={mobileStyles}
                          size={"normal"}
                          group={group}
                          loading={loading && showLoadingSpinner}
                          showAnnotations={showAnnotations}
                          cellHighlightKey={cellHighlightKey}
                          cellHighlightDates={cellHighlightDates}
                        />
                      )}
                    </Col>
                    {rightContent && showRightMetrics && <Col xs={4}>{rightContent}</Col>}
                  </>
                )}
                {chartToggle === "table" && (
                  <BulkActionsTable
                    hideFilters={true}
                    hideResetFiltersIcon={true}
                    hideFilterToggleIcon={true}
                    noDataText={`No Data Available`}
                    tableRef={tableRef}
                    tableId={`${context}Table`}
                    titleCol={{
                      id: "name",
                      Header: "Date",
                      accessor: "name",
                      style: { width: "150px" },
                      width: 150,
                      Cell: ({ value }) => {
                        return moment(value).format(group === "month" ? "MMM 'YY" : "MMM D 'YY");
                      },
                    }}
                    columnSpecs={columnSpecs}
                    data={"date" in data! ? data.date : data}
                    isSelectable={false}
                    isExpandable={false}
                    defaultSorted={[{ id: "name", desc: false }]}
                    getSearchCriteria={(row) => {
                      const { name } = row as { name: string };
                      return `${name}`;
                    }}
                    tableRowDescription={title}
                    minRows={5}
                  />
                )}
              </>
            )}
          </Row>
        }
        {children}
      </SummaryCard>
    </>
  );
};

export default MetricsCard;
